import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Download, Eye, Loader2, X } from "lucide-react";
import { api, API_BASE_URL } from "../../lib/axios";
import { CommercialProformaPreview, type CommercialProformaPreviewDto } from "./CommercialProformaPreview";

type ProformaDetail = {
  id: number;
  numero: string;
  objet: string | null;
  statut: string;
  cycleStatut: string;
  fichierPDF: string | null;
  client?: { nom?: string | null; prenom?: string | null; nomEntreprise?: string | null } | null;
};

type ProformaFull = ProformaDetail & {
  clientId: number;
  dateEmission: string;
  validiteProformaJours: number | null;
  conditionsPaiement: string | null;
  notesClient: string | null;
  mentionSpecifique: string | null;
  devise: string;
  exonereTva: boolean;
  lignes: Array<{ description: string; quantite: number | string; prixUnitaire: number | string; remise?: number | string | null }>;
};

function abs(path: string | null): string | null {
  if (!path) return null;
  if (/^https?:\/\//i.test(path)) return path;
  const root = API_BASE_URL.replace(/\/api\/?$/i, "");
  return `${root}${path.startsWith("/") ? path : `/${path}`}`;
}

function pdfUrl(file?: string | null): string | null {
  if (!file) return null;
  const base = file.replace(/\\/g, "/").split("/").pop();
  return base ? abs(`/uploads/proformas/${encodeURIComponent(base)}`) : null;
}

export default function ProformaDetailPage(): React.JSX.Element {
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(true);
  const [proforma, setProforma] = useState<ProformaDetail | null>(null);
  const [preview, setPreview] = useState<CommercialProformaPreviewDto | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const { data } = await api.get<ProformaDetail>(`/proformas/${id}`);
      setProforma(data);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  const openPreview = async () => {
    if (!proforma) return;
    setBusy(true);
    try {
      const { data: full } = await api.get<ProformaFull>(`/proformas/${proforma.id}`);
      const { data } = await api.post<{ data?: CommercialProformaPreviewDto }>("/proformas/preview", {
        id: full.id,
        numero: full.numero,
        clientId: full.clientId,
        objet: full.objet,
        dateEmission: full.dateEmission,
        validiteProformaJours: full.validiteProformaJours,
        conditionsPaiement: full.conditionsPaiement,
        notesClient: full.notesClient,
        mentionSpecifique: full.mentionSpecifique,
        devise: full.devise,
        exonereTva: full.exonereTva,
        lignes: Array.isArray(full.lignes) ? full.lignes.map((l) => ({ description: l.description, quantite: Number(l.quantite), prixUnitaire: Number(l.prixUnitaire), remise: Number(l.remise ?? 0), devise: full.devise })) : [],
        fichierPDF: full.fichierPDF,
      });
      if (data.data) setPreview(data.data);
    } finally {
      setBusy(false);
    }
  };

  const downloadPdf = async () => {
    if (!proforma) return;
    setBusy(true);
    try {
      const { data } = await api.post<{ pdfUrl?: string }>(`/proformas/${proforma.id}/generate-pdf`);
      await load();
      if (data.pdfUrl) window.open(abs(data.pdfUrl) ?? data.pdfUrl, "_blank", "noopener,noreferrer");
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="p-8 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  if (!proforma) return <div className="p-8 text-rose-300">Proforma introuvable.</div>;
  const url = pdfUrl(proforma.fichierPDF);
  const clientName = proforma.client?.nomEntreprise || [proforma.client?.prenom, proforma.client?.nom].filter(Boolean).join(" ") || "Non renseigné";

  return (
    <div className="min-h-screen bg-slate-950 p-6 text-slate-100">
      {preview ? (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black/80 p-4">
          <div className="sticky top-0 z-[51] mx-auto mb-4 flex max-w-[980px] justify-between rounded-xl border border-slate-700 bg-slate-950/95 px-4 py-3">
            <p className="font-semibold">Aperçu proforma {preview.proforma.numero}</p>
            <button onClick={() => setPreview(null)} className="inline-flex items-center gap-2 rounded-lg border border-slate-700 px-3 py-2 text-sm"><X className="h-4 w-4" /> Fermer</button>
          </div>
          <CommercialProformaPreview data={preview} />
        </div>
      ) : null}
      <div className="mx-auto max-w-3xl space-y-6 rounded-2xl border border-slate-800 bg-slate-900/80 p-8">
        <div>
          <p className="text-xs uppercase tracking-widest text-emerald-300">Finance</p>
          <h1 className="mt-2 text-2xl font-bold">Proforma {proforma.numero}</h1>
          <p className="mt-1 text-sm text-slate-400">Client : {clientName}</p>
        </div>
        <div className="grid gap-3 text-sm sm:grid-cols-2">
          <Info label="Objet" value={proforma.objet ?? "Non renseigné"} />
          <Info label="Statut" value={proforma.statut} />
          <Info label="Cycle" value={proforma.cycleStatut} />
          <Info label="Document" value={url ? "Document disponible" : "Document temporairement indisponible"} />
        </div>
        <div className="flex flex-wrap gap-3">
          <button onClick={() => void openPreview()} disabled={busy} className="inline-flex items-center gap-2 rounded-xl bg-sky-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"><Eye className="h-4 w-4" /> Voir proforma</button>
          <button onClick={() => void downloadPdf()} disabled={busy} className="inline-flex items-center gap-2 rounded-xl border border-slate-600 px-4 py-2 text-sm font-semibold disabled:opacity-50"><Download className="h-4 w-4" /> Télécharger PDF</button>
          {url ? <a href={url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white"><Download className="h-4 w-4" /> Télécharger PDF</a> : null}
          <button onClick={() => void api.post(`/proformas/${proforma.id}/convert-to-facture`)} className="rounded-xl border border-amber-500/40 px-4 py-2 text-sm font-semibold text-amber-100">Convertir en facture</button>
          <Link to="/admin/finance/proformas" className="rounded-xl border border-slate-600 px-4 py-2 text-sm font-semibold">Retour</Link>
        </div>
      </div>
    </div>
  );
}

function Info({ label, value }: { label: string; value: string }) {
  return <div><p className="text-slate-500">{label}</p><p className="font-medium text-white">{value}</p></div>;
}
